import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, AlertTriangle } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/features/auth';
import { useCompliance, useComplianceMutations } from '../hooks';
import {
  COMPLIANCE_STATUS_VALUES,
  RISK_LEVEL_VALUES,
} from '../types';
import type { ComplianceStatus, RiskLevel } from '../types';

interface ComplianceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: number | null;
  onSuccess?: () => void;
}

/**
 * Case 360° (Lot 2) Stage 6 — the Partner's manual Compliance decision + Risk
 * Level. A note is required for FAILED / BYPASSED. The firm-wide BYPASS rate is
 * surfaced; when the >10% auto-block is active (and no MD override) BYPASS is
 * disabled here (the server also enforces it).
 */
export function ComplianceDialog({
  open,
  onOpenChange,
  matterId,
  onSuccess,
}: ComplianceDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMD = user?.role?.type === 'MD';
  const { data: state, isLoading } = useCompliance(open ? matterId ?? undefined : undefined);
  const { setComplianceMutation, setBypassOverrideMutation } = useComplianceMutations();

  const [status, setStatus] = React.useState<ComplianceStatus>('PENDING');
  const [riskLevel, setRiskLevel] = React.useState<RiskLevel | ''>('');
  const [note, setNote] = React.useState('');

  React.useEffect(() => {
    if (open && state) {
      setStatus(state.compliance);
      setRiskLevel(state.riskLevel ?? '');
      setNote(state.complianceNote ?? '');
    }
  }, [open, state]);

  const bypass = state?.bypass;
  const bypassBlocked = (bypass?.blocked ?? false) && status === 'BYPASSED';
  const needsNote = status === 'FAILED' || status === 'BYPASSED';
  const noteMissing = needsNote && !note.trim();

  const canSubmit =
    !!matterId &&
    !isLoading &&
    !noteMissing &&
    !bypassBlocked &&
    !setComplianceMutation.isPending;

  const handleSubmit = async () => {
    if (!matterId || !canSubmit) return;
    await setComplianceMutation.mutateAsync({
      id: matterId,
      data: {
        status,
        ...(needsNote ? { note: note.trim() } : {}),
        ...(riskLevel ? { riskLevel } : {}),
      },
    });
    onOpenChange(false);
    onSuccess?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-indigo-500" />
            {t('compliance.dialogTitle')}
          </DialogTitle>
          <DialogDescription>{t('compliance.dialogDescription')}</DialogDescription>
        </DialogHeader>

        <div className="my-2 space-y-4">
          {/* Compliance decision */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('compliance.statusLabel')}
            </label>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as ComplianceStatus)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('compliance.statusLabel')} />
              </SelectTrigger>
              <SelectContent>
                {COMPLIANCE_STATUS_VALUES.map((s) => (
                  <SelectItem
                    key={s}
                    value={s}
                    disabled={s === 'BYPASSED' && (bypass?.blocked ?? false)}
                  >
                    {t(`compliance.status.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Risk level */}
          <div>
            <label className="mb-2 block text-sm font-medium">
              {t('compliance.riskLabel')}
            </label>
            <Select
              value={riskLevel}
              onValueChange={(v) => setRiskLevel(v as RiskLevel)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('compliance.selectRisk')} />
              </SelectTrigger>
              <SelectContent>
                {RISK_LEVEL_VALUES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {t(`compliance.risk.${r}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reason (required for FAILED / BYPASSED) */}
          {needsNote && (
            <div>
              <label className="mb-2 block text-sm font-medium">
                {t('compliance.noteLabel')}
              </label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t('compliance.notePlaceholder')}
                rows={3}
                maxLength={1000}
              />
            </div>
          )}

          {/* Firm-wide bypass rate context */}
          {bypass && bypass.level !== 'ok' && (
            <div
              className={`rounded-md border px-3 py-2 text-sm ${
                bypass.level === 'red'
                  ? 'border-red-300 bg-red-50 text-red-700'
                  : 'border-amber-300 bg-amber-50 text-amber-700'
              }`}
            >
              <div className="flex items-center gap-2 font-medium">
                <AlertTriangle className="h-4 w-4" />
                {t('compliance.bypassRate', {
                  rate: (bypass.rate * 100).toFixed(1),
                })}
              </div>
              {bypass.blocked && (
                <p className="mt-1 text-xs">{t('compliance.bypassBlocked')}</p>
              )}
              {bypass.blocked && isMD && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 border-red-300 text-red-700 hover:bg-red-100"
                  onClick={() =>
                    setBypassOverrideMutation.mutate({ mdOverride: true })
                  }
                  disabled={setBypassOverrideMutation.isPending}
                >
                  {setBypassOverrideMutation.isPending
                    ? t('processing')
                    : t('compliance.liftBlock')}
                </Button>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={setComplianceMutation.isPending}
          >
            {t('cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {setComplianceMutation.isPending
              ? t('processing')
              : t('compliance.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
